import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setDefaultCountryCode } from './themeProfileSlice'
import {
  buildDisaggregationFiltersWithT,
  formatUnitMult,
  getDateRange,
  getEndpointFromConfig,
  getRecentYearRange,
  getUnitMeasureName,
} from '../../common/utils/helper'
import { fetchAndSetData } from '../../common/commonSlice'
import { disaggregations, endpointLabels } from '../../common/utils/enum'
import WorldMapWrapper from './WorldMapWrapper'
import { WORLD_DEFAULT } from '../../common/utils/constants'
import ChartCardContentLoader from '../../common/components/CardContentLoader/ChartCardContentLoader'
import ChartRenderer from '../../common/components/ChartRenderer'
import { lineChartOptions } from './chartOptions'

const Visualization = ({
  visType,
  indicatorId,
  datasetId,
  name,
  datasetName,
  sources,
  longDefination,
  indicatorRedirectUrl,
  visualization,
  subTopicIndex,
  featuredDatasetsData,
}) => {
  const [filteredIndicatorData, setFilteredIndicatorData] = useState([])
  const [indicatorMinYear, setIndicatorMinYear] = useState(0)
  const [indicatorMaxYear, setIndicatorMaxYear] = useState(0)
  const [lineChartCountryDetails, setLineChartCountryDetails] = useState([])
  const [chartType, setChartType] = useState(visType)
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const { countryList, endpointList, featureProps, codeList } = useSelector(
    (state) => state.common
  )
  const { defaultCountryCode, countryDropdownValue } = useSelector(
    (state) => state.themeProfile
  )
  const scrollBackRef = useRef(null)
  const { noDataImageURL, authorDatasetLink, publishDatasetLink, mode } =
    featureProps

  useEffect(() => {
    const fetchData = async () => {
      if (countryList?.length) await fetchDisaggregationData()
    }
    fetchData()
  }, [countryList, countryDropdownValue])

  const fetchDisaggregationData = async () => {
    setIsLoading(true)
    try {
      let tempCountry = ''
      let datasetID =
        datasetId?.toUpperCase() === 'HCI'
          ? 'WB_HCI'
          : datasetId?.toUpperCase() === 'HCP'
          ? 'WB_HCP'
          : datasetId
      const params2 = `datasetId=${datasetID}&indicatorId=${indicatorId}`
      const endpoint = `${
        getEndpointFromConfig(endpointList, endpointLabels.Disaggregation)
          .apiEndpoint
      }?${params2}`
      const response = await dispatch(
        fetchAndSetData({
          endpoint: endpoint,
          key: endpoint,
        })
      )
      const disaggregationResponse = response?.payload
      if (disaggregationResponse?.length) {
        const disaggrigationCountries = disaggregationResponse?.find(
          (item) => item.filterName === disaggregations.RefArea
        )?.filterValues
        const isWLD = disaggrigationCountries?.find(
          (obj) => obj.id === WORLD_DEFAULT.id
        )
        let countriesForLine = [
          ...[{ id: 'WLD', name: 'World' }, ...countryList],
        ]?.filter(
          (obj) =>
            obj.id === WORLD_DEFAULT.id || obj.id === countryDropdownValue
        )
        setLineChartCountryDetails(countriesForLine)
        let tempChartType = chartType
        if (isWLD) {
          tempCountry = WORLD_DEFAULT.id
          dispatch(setDefaultCountryCode([tempCountry]))
        }
        if (tempChartType === '' || tempChartType === 'Line Chart') {
          if (isWLD) {
            tempChartType = 'Line Chart'
          } else {
            tempChartType = 'Map'
          }
        }
        setChartType(tempChartType)
        await fetchIndicatorsData(
          tempCountry,
          disaggregationResponse,
          tempChartType
        )
      }
    } catch (error) {
      console.error('Error fetching disaggregation data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchIndicatorsData = async (
    countryCode,
    disaggregationResponse,
    tempChartType
  ) => {
    try {
      let datasetID =
        datasetId?.toUpperCase() === 'HCI'
          ? 'WB_HCI'
          : datasetId?.toUpperCase() === 'HCP'
          ? 'WB_HCP'
          : datasetId

      let baseParams = `filter= DATASET='${datasetID}' AND IND_ID='${indicatorId
        .trim()
        .toUpperCase()}'`

      // Adding dimensions to filter only if dimensions are more than 1
      const { params: paramsWithT, isSecondAPICallEligible } =
        buildDisaggregationFiltersWithT(
          baseParams,
          disaggregationResponse,
          true
        )
      let finalParamsWithT = paramsWithT

      if (tempChartType === 'Line Chart') {
        if (countryDropdownValue) {
          finalParamsWithT = `${finalParamsWithT} AND REF_AREA='${countryCode},${countryDropdownValue}'`
        } else {
          finalParamsWithT = `${finalParamsWithT} AND REF_AREA='${countryCode}'`
        }
      } else {
        finalParamsWithT += ` AND LATEST='true'`
      }

      const response = await dispatch(
        fetchAndSetData({
          endpoint: `${
            getEndpointFromConfig(endpointList, endpointLabels.Data).apiEndpoint
          }?${finalParamsWithT}`,
          key: finalParamsWithT,
        })
      )
      if (response?.payload?.length) {
        const allCountries = [...countryList, WORLD_DEFAULT].map(
          (obj) => obj.id
        )
        // const indicatorResponse = response?.payload.filter((obj) => {
        //   return allCountries.includes(obj.countryCode)
        // })
        const indicatorResponse = allCountries
          .map((obj) => {
            return response?.payload.find(
              (objRes) => objRes.countryCode === obj
            )
          })
          .filter(Boolean)
        setFilteredIndicatorData(indicatorResponse)
        getRecentYear(indicatorResponse)
      } else if (isSecondAPICallEligible) {
        const { params: paramsWithoutT } = buildDisaggregationFiltersWithT(
          baseParams,
          disaggregationResponse,
          false
        )
        let finalParamsWithoutT = paramsWithoutT

        if (tempChartType === 'Line Chart') {
          if (countryDropdownValue) {
            finalParamsWithoutT = `${finalParamsWithoutT} AND REF_AREA='${countryCode},${countryDropdownValue}'`
          } else {
            finalParamsWithoutT = `${finalParamsWithoutT} AND REF_AREA='${countryCode}'`
          }
        } else {
          finalParamsWithoutT += ` AND LATEST='true'`
        }

        const response = await dispatch(
          fetchAndSetData({
            endpoint: `${
              getEndpointFromConfig(endpointList, endpointLabels.Data)
                .apiEndpoint
            }?${finalParamsWithoutT}`,
            key: finalParamsWithoutT,
          })
        )
        if (response?.payload?.length) {
          const allCountries = [...countryList, WORLD_DEFAULT].map(
            (obj) => obj.id
          )
          // const indicatorResponse = response?.payload.filter((obj) => {
          //   return allCountries.includes(obj.countryCode)
          // })
          const indicatorResponse = allCountries
            .map((obj) => {
              return response?.payload.find(
                (objRes) => objRes.countryCode === obj
              )
            })
            .filter(Boolean)
          setFilteredIndicatorData(indicatorResponse)
          getRecentYear(indicatorResponse)
        } else {
          setFilteredIndicatorData([])
          getRecentYear([])
        }
      } else {
        setFilteredIndicatorData([])
        getRecentYear([])
      }
    } catch (error) {
      console.error('Error fetching indicator data:', error)
    }
  }

  const getRecentYear = (filteredData) => {
    const { minYear, maxYear } = getDateRange(filteredData)
    setIndicatorMaxYear(maxYear)
    setIndicatorMinYear(minYear)
    if (minYear === Infinity && maxYear === -Infinity) {
      setFilteredIndicatorData([])
    }
  }

  let datasetUrl = ''
  if (mode) {
    const temp = [defaultCountryCode].filter(Boolean)

    if (countryDropdownValue && !temp.includes(countryDropdownValue)) {
      temp.push(countryDropdownValue)
    }

    const query = temp.length ? `?country=${temp.join()}` : ''

    datasetUrl =
      mode === 'author'
        ? `${authorDatasetLink}.${datasetId}${query}.html`
        : `${publishDatasetLink}/${datasetId}${query}`
  }

  const unitMeasureName = getUnitMeasureName(codeList, filteredIndicatorData)
  const yearRange = getRecentYearRange(filteredIndicatorData)

  return (
    <div
      className="swiper-slide tui_graph_Card swiper-slide-active"
      key={indicatorId}
    >
      <div className="tui_graphcard_wrapper">
        <div className="tui_card_content">
          <div className="tui_text_md">
            <a
              href={`${indicatorRedirectUrl}${indicatorId}${
                countryDropdownValue ? `?country=${countryDropdownValue}` : ''
              }`}
              data-customlink="nl:body content"
            >
              {name}
            </a>
          </div>
          <div className="tui_text_base tui_unit_measure">
            {(yearRange || unitMeasureName) && (
              <p>{`${yearRange ? `Most recent values ${yearRange}` : ''}${
                yearRange && unitMeasureName ? ', ' : ''
              }${unitMeasureName ? unitMeasureName : ''}`}</p>
            )}
          </div>
        </div>
        <div className="tui_graph_wrapper" key={`${indicatorId}-${chartType}`}>
          {isLoading ? (
            <ChartCardContentLoader />
          ) : chartType === 'Map' ? (
            <WorldMapWrapper
              filteredIndicatorData={filteredIndicatorData}
              selectedSingleYear={indicatorMaxYear}
              indicatorCode={indicatorId}
              uniqueKey={`${indicatorId}-${subTopicIndex}-world-map`}
              visualization={visualization}
              selectedCountries={defaultCountryCode}
              countryDropdownValue={countryDropdownValue}
              isTheme={true}
            />
          ) : chartType === 'Line Chart' ? (
            isLoading ? (
              <ChartCardContentLoader />
            ) : (
              <ChartRenderer
                id={`topic-profile-${chartType}-${indicatorId}`}
                options={lineChartOptions(
                  filteredIndicatorData,
                  '',
                  '',
                  false,
                  lineChartCountryDetails,
                  countryDropdownValue,
                  unitMeasureName,
                  visualization,
                  formatUnitMult(codeList, filteredIndicatorData?.[0]?.unitMult)
                )}
              />
            )
          ) : (
            <img
              style={{ height: '345px', width: '100%' }}
              src={`${noDataImageURL}`}
              alt="Data is not available"
            />
          )}
        </div>
        {datasetName && (
          <p>
            <strong>Dataset: </strong>
            <a href={datasetUrl} data-customlink="nl:body content">
              {datasetName}
            </a>
          </p>
        )}
        {sources?.length > 0 && (
          <p>
            <strong>Source: </strong>
            {sources.map((item, index) => (
              <span key={`source-${index}`}>
                {index > 0 && '; '}
                {item.sourceLink ? (
                  <a
                    href={item.sourceLink}
                    target="_blank"
                    data-customlink="nl:body content"
                  >
                    {item.source}
                  </a>
                ) : (
                  <span>{item.source}</span>
                )}
              </span>
            ))}
          </p>
        )}
      </div>
    </div>
  )
}

export default Visualization
